import React, { useState, useRef } from 'react';
import { UploadCloudIcon, FileIcon, XIcon, CheckCircleIcon, AlertCircleIcon, DownloadIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
const BulkDisputeUpload: React.FC = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'validating' | 'success' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadHistory = [{
    id: 'UPL-001',
    date: '2023-10-25 09:15',
    filename: 'visa_disputes_oct.csv',
    records: 145,
    status: 'Completed',
    errors: 0
  }, {
    id: 'UPL-002',
    date: '2023-10-24 14:30',
    filename: 'mastercard_cb_q3.xlsx',
    records: 89,
    status: 'Completed with Errors',
    errors: 3
  }, {
    id: 'UPL-003',
    date: '2023-10-20 11:00',
    filename: 'unionpay_retrievals.csv',
    records: 42,
    status: 'Completed',
    errors: 0
  }];
  const columns = [{
    header: 'Upload ID',
    accessor: 'id',
    className: 'font-medium text-gray-900 dark:text-white'
  }, {
    header: 'Date & Time',
    accessor: 'date'
  }, {
    header: 'File Name',
    accessor: 'filename'
  }, {
    header: 'Total Records',
    accessor: 'records'
  }, {
    header: 'Errors',
    accessor: (row: any) => row.errors > 0 ? <span className="text-red-600 font-medium" data-id="element-3642">{row.errors}</span> : '0'
  }, {
    header: 'Status',
    accessor: (row: any) => <StatusBadge status={row.status === 'Completed' ? 'success' : 'warning'} text={row.status} data-id="element-3643" />
  }];
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (selectedFile: File) => {
    // Validate file type
    if (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx')) {
      setFile(selectedFile);
      setUploadStatus('idle');
    } else {
      alert('Please upload a valid CSV or Excel file.');
    }
  };
  const handleUpload = () => {
    if (!file) return;
    setUploadStatus('validating');
    // Mock upload and validation process
    setTimeout(() => {
      setUploadStatus('success');
    }, 2000);
  };
  return <div className="space-y-6" data-id="element-3644">
      <div data-id="element-3645">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3646">
          Bulk Dispute Ingestion
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3647">
          Upload scheme dispute files (CSV/Excel) to automatically create and
          assign disputes.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3648">
        <div className="lg:col-span-2" data-id="element-3649">
          <Card title="Upload File" data-id="element-3650">
            {!file ? <div className={`mt-2 flex justify-center px-6 pt-10 pb-12 border-2 border-dashed rounded-lg transition-colors ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-3651">
                <div className="space-y-1 text-center" data-id="element-3652">
                  <UploadCloudIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-3653" />
                  <div className="flex text-sm text-gray-600 dark:text-gray-400 justify-center" data-id="element-3654">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 px-1" data-id="element-3655">
                      <span data-id="element-3656">Upload a file</span>
                      <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" ref={fileInputRef} onChange={e => e.target.files && handleFileSelect(e.target.files[0])} data-id="element-3657" />
                    </label>
                    <p className="pl-1" data-id="element-3658">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-3659">
                    CSV or Excel files up to 10MB
                  </p>
                </div>
              </div> : <div className="mt-2" data-id="element-3660">
                <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50" data-id="element-3661">
                  <div className="flex items-center" data-id="element-3662">
                    <FileIcon className="h-8 w-8 text-blue-500 mr-3" data-id="element-3663" />
                    <div data-id="element-3664">
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3665">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-3666">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  {uploadStatus === 'idle' && <button onClick={() => setFile(null)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-3667">
                      <XIcon className="h-5 w-5" data-id="element-3668" />
                    </button>}
                </div>

                {uploadStatus === 'validating' && <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md flex items-center" data-id="element-3669">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3" data-id="element-3670"></div>
                    <p className="text-sm text-blue-700 dark:text-blue-300" data-id="element-3671">
                      Validating file format, mandatory fields, and transaction
                      existence...
                    </p>
                  </div>}

                {uploadStatus === 'success' && <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800" data-id="element-3672">
                    <div className="flex" data-id="element-3673">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-3" data-id="element-3674" />
                      <div data-id="element-3675">
                        <h3 className="text-sm font-medium text-green-800 dark:text-green-300" data-id="element-3676">
                          Upload Successful
                        </h3>
                        <p className="mt-1 text-sm text-green-700 dark:text-green-400" data-id="element-3677">
                          Successfully processed 145 records. 145 disputes have
                          been created and merchants notified.
                        </p>
                        <button onClick={() => {
                    setFile(null);
                    setUploadStatus('idle');
                  }} className="mt-3 text-sm font-medium text-green-800 dark:text-green-300 hover:underline" data-id="element-3678">
                          Upload another file
                        </button>
                      </div>
                    </div>
                  </div>}

                {uploadStatus === 'idle' && <div className="mt-6 flex justify-end" data-id="element-3679">
                    <button onClick={handleUpload} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-3680">
                      Process File
                    </button>
                  </div>}
              </div>}
          </Card>
        </div>

        <div className="lg:col-span-1" data-id="element-3681">
          <Card title="File Requirements" data-id="element-3682">
            <div className="space-y-4" data-id="element-3683">
              <div className="flex items-start" data-id="element-3684">
                <AlertCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-3685" />
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3686">
                  The system validates transaction existence and merchant
                  mapping automatically.
                </p>
              </div>
              <div data-id="element-3687">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2" data-id="element-3688">
                  Mandatory Fields:
                </h4>
                <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 space-y-1" data-id="element-3689">
                  <li data-id="element-3690">Transaction ID</li>
                  <li data-id="element-3691">Card Masked Number</li>
                  <li data-id="element-3692">Transaction Date</li>
                  <li data-id="element-3693">Dispute Amount</li>
                  <li data-id="element-3694">Dispute Type</li>
                  <li data-id="element-3695">Reason Code</li>
                  <li data-id="element-3696">Merchant ID</li>
                  <li data-id="element-3697">Customer Claim Date</li>
                </ul>
              </div>
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3698">
                <a href="#" className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-3699">
                  <DownloadIcon className="h-4 w-4 mr-1" data-id="element-3700" />
                  Download CSV Template
                </a>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Card title="Recent Uploads" data-id="element-3701">
        <Table columns={columns} data={uploadHistory} keyField="id" pagination={false} data-id="element-3702" />
      </Card>
    </div>;
};
export default BulkDisputeUpload;